import { useConfig } from "../../../contexts/ConfigContext"
import React, { useEffect, useState } from "react"
import { useRegister } from "../../../contexts/RegisterContext"
import { message, Popover, Tag, TableColumnsType, Tooltip, Avatar, Image, Button } from "antd"
import DataViewList from "../DataViewList"
import { contentPath, modules } from "../../../config/pages"
import { useItems, useStoreFetch } from "../../../hooks/useInventory"
import useAuth from "../../../hooks/useAuth"
import { useCompany } from "../../../contexts/CompanyContext"
import { ColumnType } from "antd/es/table"
import {
  SearchOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import HeaderElements from "./HeaderElements"
import { useHistory } from "react-router-dom"
import { useStore } from "../../../contexts/StoreContext"
import { TInventoryFirestore } from "../../../models/storageModel"
import { ViewItemModal } from "../../modals/ViewItemModal"

function ViewStorageItemList() {
  const { config, updateConfig } = useConfig()
  const { register, updateRegister } = useRegister()
  const { company } = useCompany()
  const { store, updateStore } = useStore()
  const history = useHistory()
  const { tokenData } = useAuth.useToken("authCsrfToken", config.session, config.key)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(30)

  // Modal state
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [modalVisible, setModalVisible] = useState(false)

  const { data, error, isLoading } = useItems(tokenData, {
    company: company.current.uid,
    module: config.module,
    page,
    hitsPerPage: pageSize,
  })
  const { data: storeData } = useStoreFetch(tokenData, {
    all: true,
    company: company.current.uid,
  })
  const [skeleton, setSkeleton] = useState(true)
  const title = "Elementos de almacén"
  const emptyMessage = "Aún no has agregado elementos al almacén"

  useEffect(() => {
    if (tokenData && data?.itemList) {
      updateRegister({ registerList: data.itemList })
      setSkeleton(false)
    }
    if (storeData?.storehouse) {
      updateStore({ list: storeData.storehouse })
    }
  }, [tokenData, data, storeData])

  const handleClick = (rowData: any) => {
    setSelectedItem(rowData)
    setModalVisible(true)
  }

  // Columnas de la tabla con búsqueda
  const filterIconElement = (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
  )
  const getColumnSearchProps = (dataIndex: string): ColumnType => ({
    filterIcon: filterIconElement,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toString().toLowerCase())
        : false,
  })

  const getCategoryName = (category: string) => {
    const moduleOption = register.categoryOptions.find(
      (option) => option.type === "CATEGORY_OPTION" && option.key === category
    )
    return moduleOption?.string_value || category || ""
  }

  const renderQuantity = (
    inventory: TInventoryFirestore[],
    register_type: string,
    minQuantity: number = 0,
    displayUnit: string = "UND"
  ) => {
    if (register_type === "SERVICE") {
      return <Tag color="default">No aplica</Tag>
    }

    const total =
      !inventory || inventory.length === 0
        ? 0
        : inventory.reduce((sum, entry) => sum + (entry.quantity || 0), 0)

    let badge = null
    const unitLabel = displayUnit || "UND"

    if (total < 0) {
      badge = (
        <Tooltip title="Se hicieron movimientos sin tener unidades suficientes. Debes reabastecer con un movimiento de entrada.">
          <Tag
            color="error"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 600,
              borderRadius: 4,
              margin: 0,
            }}
          >
            <CloseCircleOutlined />
            {total} {unitLabel}
          </Tag>
        </Tooltip>
      )
    } else if (total === 0) {
      badge = (
        <Tooltip title="No hay unidades disponibles. Puedes agregarlas desde el módulo de Storage haciendo un movimiento de entrada.">
          <Tag
            color="warning"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 600,
              borderRadius: 4,
              margin: 0,
            }}
          >
            <ExclamationCircleOutlined />
            Agotado (0)
          </Tag>
        </Tooltip>
      )
    } else if (minQuantity > 0 && total <= minQuantity) {
      badge = (
        <Tooltip title={`Stock bajo: El nivel actual (${total}) es menor o igual al stock mínimo configurado (${minQuantity}).`}>
          <Tag
            color="gold"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 600,
              borderRadius: 4,
              margin: 0,
            }}
          >
            <WarningOutlined />
            {total} {unitLabel} (Bajo)
          </Tag>
        </Tooltip>
      )
    } else {
      badge = (
        <Tag
          color="success"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontWeight: 600,
            borderRadius: 4,
            margin: 0,
          }}
        >
          <CheckCircleOutlined />
          {total} {unitLabel}
        </Tag>
      )
    }

    if (!inventory || inventory.length <= 1) {
      return badge
    }

    const content = (
      <div style={{ minWidth: 200 }}>
        <div style={{ fontWeight: 600, marginBottom: 8, borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}>
          Existencias por almacén ({total} {unitLabel})
        </div>
        {inventory.map((entry) => {
          const storeName = store.list.find((s) => s.uid === entry.uid)?.name || entry.uid
          return (
            <div
              key={entry.uid}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                padding: "3px 0",
                fontSize: 13,
              }}
            >
              <span style={{ color: "#595959" }}>{storeName}</span>
              <strong>{entry.quantity}</strong>
            </div>
          )
        })}
      </div>
    )

    return (
      <Popover content={content} trigger="hover">
        <div style={{ cursor: "pointer", display: "inline-block" }}>{badge}</div>
      </Popover>
    )
  }

  const getSellPrice = (record: any) => {
    if (record?.prices && Array.isArray(record.prices)) {
      const p = record.prices.find((item: any) => item.type === "SELL")
      if (p && p.value !== undefined && p.value !== null) return p.value
    }
    return record?.sell_price
  }

  const getPurchasePrice = (record: any) => {
    if (record?.prices && Array.isArray(record.prices)) {
      const p = record.prices.find((item: any) => item.type === "PURCHASE")
      if (p && p.value !== undefined && p.value !== null) return p.value
    }
    return record?.purchase_price
  }

  const tableColumns: TableColumnsType = [
    {
      title: "#",
      dataIndex: "register",
      width: 55,
      align: "center",
      render: (val: number) => <span style={{ color: "#8c8c8c", fontSize: 12 }}>{val}</span>,
    },
    {
      title: "Producto / Referencia",
      dataIndex: "name",
      width: 300,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      ...getColumnSearchProps("name"),
      render: (_: any, record: any) => {
        const primaryImg = record.images?.find((img: any) => img.is_primary) || record.images?.[0]
        const imageUrl = record.image_mini || record.image || primaryImg?.file_url
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{ flexShrink: 0 }}
              onClick={(e) => {
                if (imageUrl) e.stopPropagation()
              }}
            >
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={record.name}
                  width={44}
                  height={44}
                  preview={{
                    mask: <EyeOutlined style={{ fontSize: 13 }} />,
                  }}
                  style={{
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #f0f0f0",
                    background: "#fafafa",
                  }}
                  fallback="assets/images/no-data-image.svg"
                />
              ) : (
                <Avatar
                  shape="square"
                  size={44}
                  icon={<InboxOutlined style={{ fontSize: 22, color: "#bfbfbf" }} />}
                  style={{
                    backgroundColor: "#fafafa",
                    borderRadius: 6,
                    border: "1px solid #e8e8e8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              )}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: "#262626",
                  lineHeight: "1.35",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={record.name}
              >
                {record.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#8c8c8c",
                  marginTop: 2,
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span>ID: {record.code}</span>
                {record.barcode && <span>• EAN: {record.barcode}</span>}
                {record.register_type === "SERVICE" && (
                  <Tag color="cyan" style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px", margin: 0 }}>
                    Servicio
                  </Tag>
                )}
                {record.register_type === "FIXED_ASSET" && (
                  <Tag color="purple" style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px", margin: 0 }}>
                    Activo fijo
                  </Tag>
                )}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Categoría",
      dataIndex: "category",
      width: 140,
      sorter: (a, b) => getCategoryName(a.category).localeCompare(getCategoryName(b.category)),
      render: (text: string) => {
        const catName = getCategoryName(text)
        return catName ? (
          <Tag color="blue" style={{ borderRadius: 4, margin: 0 }}>
            {catName}
          </Tag>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        )
      },
    },
    {
      title: "Presentación/U.M.",
      dataIndex: "display",
      width: 135,
      render: (_: string, record: any) => {
        const isService = record.register_type === "SERVICE"
        const measureUnit = record.measure_unit || "UND"
        const display = record.display

        // 1. Para servicios: solo se muestra la unidad de cobro/medida
        if (isService) {
          return (
            <Tag color="default" style={{ borderRadius: 4, margin: 0, fontWeight: 500 }}>
              {measureUnit}
            </Tag>
          )
        }

        // 2. Si no hay presentación o es idéntica a la unidad de medida: 1 solo tag
        if (!display || display.toUpperCase() === measureUnit.toUpperCase()) {
          return (
            <Tag color="default" style={{ borderRadius: 4, margin: 0, fontWeight: 500 }}>
              {display || measureUnit}
            </Tag>
          )
        }

        // 3. Presentación compuesta (Caja, Bulto, etc.) con unidad base
        const factor = record.conversion_factor && Number(record.conversion_factor) > 1
          ? `${record.conversion_factor} `
          : ""

        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
            <Tag color="blue" style={{ borderRadius: 4, margin: 0, fontWeight: 500, fontSize: 11.5 }}>
              {display}
            </Tag>
            <span style={{ fontSize: 11, color: "#8c8c8c", paddingLeft: 2 }}>
              Base: {factor}{measureUnit}
            </span>
          </div>
        )
      },
    },
    {
      title: "Existencias",
      dataIndex: "inventory",
      width: 150,
      render: (inventory: TInventoryFirestore[], record: any) =>
        renderQuantity(inventory, record.register_type, record.min_quantity, record.display || record.measure_unit),
    },
    {
      title: "Precio",
      dataIndex: "sell_price",
      width: 140,
      sorter: (a, b) => (getSellPrice(a) || 0) - (getSellPrice(b) || 0),
      render: (_: any, record: any) => {
        const sellPrice = getSellPrice(record)
        const purchasePrice = getPurchasePrice(record)
        if (sellPrice !== undefined && sellPrice !== null && Number(sellPrice) > 0) {
          return (
            <span style={{ fontWeight: 600, color: "#1890ff", fontSize: 13.5 }}>
              ${Number(sellPrice).toLocaleString("es-CO")}{" "}
              <span style={{ fontSize: 10, color: "#8c8c8c", fontWeight: 400 }}>COP</span>
            </span>
          )
        }
        if (purchasePrice !== undefined && purchasePrice !== null && Number(purchasePrice) > 0) {
          return (
            <Tooltip title="Precio de compra">
              <span style={{ color: "#595959", fontSize: 13 }}>
                ${Number(purchasePrice).toLocaleString("es-CO")}{" "}
                <span style={{ fontSize: 10, color: "#8c8c8c" }}>compra</span>
              </span>
            </Tooltip>
          )
        }
        return <span style={{ color: "#bfbfbf" }}>—</span>
      },
    },
    {
      title: "Ubicación",
      dataIndex: "location",
      width: 120,
      render: (text: string) =>
        text ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#595959", fontSize: 13 }}>
            <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
            {text}
          </span>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 75,
      align: "center",
      render: (_: any, record: any) => (
        <Tooltip title="Ver detalle / Editar">
          <Button
            type="text"
            shape="circle"
            icon={<EyeOutlined style={{ color: "#1890ff", fontSize: 15 }} />}
            onClick={(e) => {
              e.stopPropagation()
              handleClick(record)
            }}
          />
        </Tooltip>
      ),
    },
  ]

  const dataTable = register.registerList.map((el: any, i: number) => {
    el.code = el.custom_id || el.uid
    el.register = i + 1
    el.key = el.uid || i
    return el
  })

  const handleButtonClick = () => {
    if (store.list.length === 0) {
      message.error("Debe crear almenos un almacén antes de generar elementos")
      return
    }
    updateConfig({ module: modules.RegisterStorageItem })
    history.push(contentPath[modules.RegisterStorageItem])
  }

  const handlePageChange = (newPage: number, newPageSize?: number) => {
    setPage(newPage - 1)
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setPage(0)
    }
  }

  return (
    <>
      <DataViewList
        title={title}
        spinning={(!data && !error) || isLoading}
        skeleton={skeleton}
        tableColumns={tableColumns}
        dataTable={dataTable}
        headerElement={<HeaderElements onAddProduct={handleButtonClick} />}
        pagination={{
          current: page + 1,
          pageSize,
          total: data?.nbHits || 0,
          onChange: handlePageChange,
          showSizeChanger: false,
          showTotal: (total: number) => `Total ${total} elementos`,
        }}
        onRow={(data: any) => {
          return {
            onClick: () => handleClick(data),
            style: { cursor: "pointer" },
          }
        }}
        emptyMessage={emptyMessage}
      />

      <ViewItemModal
        visible={modalVisible}
        item={selectedItem}
        canEdit={true}
        onCancel={() => {
          setModalVisible(false)
          setSelectedItem(null)
          updateRegister({ currentStorageItem: undefined })
        }}
        onSuccess={() => {
          setModalVisible(false)
          setSelectedItem(null)
        }}
      />
    </>
  )
}

export default ViewStorageItemList